import { Injectable } from '@angular/core';
import { ContextService } from '@core/services/core/context.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { FileHelperService } from '@core/services/helpers/file-helper.service';
import { ProfileService } from '@core/services/http-services/profile.service';
import { Platform } from '@ionic/angular';
import { ProfileUserInfo } from '@models/profile';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import {
  CustomNotificationId,
  documentCaptureNotifications
} from '../../models/notification-pg';
import { AppAuthPreferences } from '../core/auth/auth.actions';
import { SecureStoreService } from '../core/storage/secure.service';
import { UiHelperService } from '../core/ui-helper.service';
import { NotificationHelperService } from './notification-helper.service';


@Injectable({
  providedIn: 'root'
})
export class ProfilesHelperService {
  availableSigningKey = false;

  constructor(
    private translate: TranslateService,
    private ui: UiHelperService,
    private secure: SecureStoreService,
    private platform: Platform,
    private notificationHelper: NotificationHelperService,
    private profileService: ProfileService,
    private nav: NavigationHelper,
    private mmobileHelper: MMobileHelperService,
    private ctx: ContextService,
    private fileHelper: FileHelperService
  ) {}

  canGoToMobileSelectorPage() {
    const tabletBreakpoint = 568;
    return this.ctx.isApp && this.platform.width() <= tabletBreakpoint;
  }

  getDocumentCaptureNotifications$() {
    return this.notificationHelper.notificationMap$.pipe(
      map(notificationMap => Object.values(notificationMap)),
      map(notifications =>
        notifications.filter(({ id }) =>
          documentCaptureNotifications.includes(id as CustomNotificationId)
        )
      )
    );
  }

  openErrorAlert(message?: string, onCancel?: () => void, cancelText?: string) {
    let title = '';
    let text = '';
    let textButton = '';
    const image = 'assets/images/profile/error.svg';
    this.translate.get('PROFILE.ERROR.TITLE').subscribe((value: string) => {
      title = value;
    });
    if (message) {
      text = message;
    } else {
      this.translate.get('PROFILE.ERROR.MESSAGE').subscribe((value: string) => {
        text = value;
      });
    }

    this.translate.get('PROFILE.ERROR.UNDERSTOOD').subscribe((value: string) => {
      textButton = value;
    });
    this.ui.presentProfileCustomAlert(
      title, image, text, null, () => {}, textButton, onCancel, cancelText
    );
  }

  async getAuthPreferences() {
    return (await this.secure.getNotSecure(
      'AUTH__APP_PREFERENCES'
    )) as AppAuthPreferences;
  }

  isTablet() {
    if (this.platform.width() > 568) {
      return true;
    }
  }

  obfuscatePhone(phone) {
    const obfuscated = '••••••';
    return `${obfuscated}${phone.slice(6, 9)}`;
  }

  getTranslatedKeys(title: string, tries?: number) {
    return this.translate.instant(title, { tries });
  }

  showSigningKeySection() {
    return this.mmobileHelper.checkIsFeatureEnabled('signingKey');
  }

  navToBlockedKeyOrValidateCode(
    header: string,
    status: string,
    operative: string,
    user: ProfileUserInfo
  ) {
    if (!this.profileService.checkSinginKeyBlockByTimeStampIsExpired()) {
      this.nav.to('profile/signing-key-result', {
        header,
        i18: 'PROFILE.SECURITY.SIGNING_KEY.BLOCK_KEY_SMS'
      });
    } else {
      this.nav.to('profile/validate-code', {
        status,
        user,
        operative,
        header
      });
    }
  }

  downloadDocument(file, name?: string) {
    const fileName = file.name || name;
    this.fileHelper
      .getDocumentFromBase64(file.base64Content, 'application/pdf', fileName)
      .then(document => this.fileHelper.open(document).catch(() => { }));
    this.ui.dismissLoading('car-sinister-consultation/document');
  }

  getFilePdf(base64, fileName) {
    return {
      base64Content: base64,
      mimeType: 'application/pdf',
      name: fileName
    };
  }

  openErrorAlertDownloadContract() {
    return this.ui.presentOneButtonAlert({
      image: 'assets/images/result/error.svg',
      title: 'DOCUMENTS.ERROR.NOT_FOUND_TITLE',
      description: 'DOCUMENTS.ERROR.NOT_FOUND_DESCRIPTION',
      onSuccess: null,
      okText: 'UNDERSTOOD'
    });
  }
}
